// @flow
import fp from "lodash/fp"
import { createSelector } from "reselect"

import { RESERVATION_VARIANT } from "booking/scenes/ReservationForm/consts/reservationVariantConsts"
import type { BookingId } from "mmb/types/primitiveTypes"

// FIXME: move?
import type { Passengers } from "booking/services/reservation/types"
import type { Action } from "booking/services/states/types"
import type { DocumentNeed } from "common/services/document/mapperDocumentNeed"
import type { InsuranceNumber } from "common/consts/insurance"
import type { State } from "booking/services/types"

export type PreBooking = {
  state: string,
  lastPayload: Object, // eslint-disable-line flowtype/no-weak-types
}

export type PassengerName = {
  firstname?: string,
  middlename?: string,
  lastname?: string,
}

export type Originals = {
  originalPassengers: ?Passengers,
  originalPassengersNames: Array<PassengerName>,
  originalEmail: ?string,
  originalPrice: ?number,
  // eslint-disable-next-line flowtype/no-weak-types
  originalInsurance: ?Array<InsuranceNumber>,
  originalPromocode: ?string,
  searchBags: ?string,
}

export type Thresholds = {
  adult: number,
  teen: number,
  child: number,
}

export type States = {
  bookingToken: ?string,
  bookingId: ?BookingId,
  authToken: ?string,
  backToSearchUrl: ?string,
  flightsCheckForced: boolean,
  loading: boolean,
  reservationVariant: string,
  originals: Originals,
  submitting: boolean,
  submittedTimestamp: ?Date,
  flightsChecked: boolean,
  billingDetails: boolean,
  maxPassengers: ?number,
  documentNeed: ?DocumentNeed,
  sandbox: boolean,
  thresholds: ?Thresholds,
  // eslint-disable-next-line flowtype/no-weak-types
  passengers: ?any, // FIXME: remove in next task?
  handBagsWithPrioBoardingAllowed: boolean,
  preBooking: ?PreBooking,
}

const DEFAULT_STATE: States = {
  bookingToken: null,
  bookingId: null,
  authToken: null,
  backToSearchUrl: null,
  flightsCheckForced: false,
  loading: true,
  reservationVariant: RESERVATION_VARIANT.SINGLESTEP,
  activeStep: 0,
  originals: {
    originalPassengers: null,
    originalPassengersNames: [],
    originalEmail: "",
    originalPrice: null,
    originalHandBags: null,
    originalHoldBags: null,
    searchBags: null,
    originalInsurance: null,
    originalPromocode: null,
  },
  session: {
    created: null,
    valid: true,
  },
  preBooking: null,
  submitting: false,
  submittedTimestamp: null,
  flightsChecked: false,
  billingDetails: false,
  maxPassengers: null,
  documentNeed: null,
  sandbox: false,
  passengers: null,
  thresholds: null,
  handBagsWithPrioBoardingAllowed: false,
}

const statesReducer = (state: States = DEFAULT_STATE, action: Action): States => {
  switch (action.type) {
    case "SET_LOADING":
      return fp.set("loading", action.payload, state)
    case "SET_SUBMITTING":
      return fp.set("submitting", action.payload, state)
    case "SET_SUBMITTING_WITH_TIMESTAMP":
      return fp.compose(
        fp.set("submittedTimestamp", +new Date()),
        fp.set("submitting", action.payload),
      )(state)
    case "SET_ACTIVE_STEP":
      return fp.set("activeStep", action.payload, state)
    case "SET_FLIGHTS_CHECKED":
      return fp.set("flightsChecked", action.payload, state)
    case "SET_BILLING_DETAILS":
      return fp.set("billingDetails", action.payload, state)
    case "TOGGLE_BILLING_DETAILS":
      return fp.set("billingDetails", !state.billingDetails, state)
    case "SET_RESERVATION_VARIANT":
      return fp.set("reservationVariant", action.payload, state)
    case "SET_PREBOOKING_STATE":
      return fp.set(
        "preBooking",
        {
          state: action.payload.state,
          lastPayload: action.payload.lastPayload
            ? action.payload.lastPayload
            : state.preBooking && state.preBooking.lastPayload,
        },
        state,
      )
    // Composed actions
    case "INIT_STATES":
      return fp.compose(
        fp.set("bookingToken", action.payload.bookingToken),
        fp.set("backToSearchUrl", action.payload.backToSearchUrl),
        fp.set("activeStep", action.payload.activeStep),
        fp.set("originals", action.payload.originals),
        fp.set("session.created", +new Date()),
      )(state)
    case "LOAD_SUCCESS_STATES":
      return fp.compose(
        fp.set("bookingToken", action.payload.bookingToken),
        fp.set("maxPassengers", action.payload.maxPassengers),
        fp.set("documentNeed", action.payload.documentNeed),
        fp.set("flightsChecked", action.payload.flightsChecked),
        fp.set("refFlags", action.payload.refFlags),
        fp.set("thresholds", action.payload.thresholds),
        // $FlowFixMe
        fp.set("handBagsWithPrioBoardingAllowed", action.payload.handBagsWithPrioBoardingAllowed),
      )(state)
    case "UPDATE_SUCCESS_STATES":
      return fp.compose(
        fp.set("bookingToken", action.payload.bookingToken),
        fp.set("flightsChecked", action.payload.flightsChecked),
      )(state)
    case "SAVE_SUCCESS_STATES":
      return fp.compose(
        fp.set("bookingId", action.payload.bookingId),
        fp.set("authToken", action.payload.authToken),
        fp.set("sandbox", action.payload.sandbox),
        fp.set("passengers", action.payload.passengers),
      )(state)
    case "SET_FLIGHTS_CHECK_FORCED":
      return fp.set("flightsCheckForced", true, state)
    case "SET_SESSION_INVALID":
      return fp.set("session.valid", false, state)
    case "SET_SESSION_VALID":
      return fp.set("session", {
        valid: true,
        created: +new Date(),
      })(state)
    case "REMOVE_ORIGINAL_PROMOCODE":
      return fp.set(["originals", "originalPromocode"], null, state)
    default:
      return state
  }
}

export default statesReducer

// Data selectors
// ===
const getStates = (state: State) => state.reservation.states

export const getBookingToken: $FlowFixMe = createSelector(
  getStates,
  states => states.bookingToken,
)
export const getBookingId: $FlowFixMe = createSelector(
  getStates,
  states => states.bookingId,
)
export const getAuthToken: $FlowFixMe = createSelector(
  getStates,
  states => states.authToken,
)
export const getBackToSearchUrl: $FlowFixMe = createSelector(
  getStates,
  states => states.backToSearchUrl,
)
export const getLoading: $FlowFixMe = createSelector(
  getStates,
  states => states.loading,
)
export const getSession: $FlowFixMe = createSelector(
  getStates,
  states => states.session,
)
export const getRefFlags: $FlowFixMe = createSelector(
  getStates,
  states => states.refFlags,
)

export const getThresholds: $FlowFixMe = createSelector(
  getStates,
  states => states.thresholds,
)
export const getAdultThreshold: $FlowFixMe = createSelector(
  getThresholds,
  states => states.adult,
)
export const getTeenThreshold: $FlowFixMe = createSelector(
  getThresholds,
  states => states.teen,
)
export const getChildThreshold: $FlowFixMe = createSelector(
  getThresholds,
  states => states.child,
)
export const getHandBagsWithPrioBoardingAllowed: $FlowFixMe = createSelector(
  getStates,
  states => states.handBagsWithPrioBoardingAllowed,
)

// Originals
const getOriginals = (state: State) => getStates(state).originals
export const getOriginalPassengers: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalPassengers || {},
)
export const getOriginalPassengersCount: $FlowFixMe = createSelector(
  getOriginalPassengers,
  fp.compose(
    fp.sum,
    fp.values,
  ),
)
export const getOriginalPrice: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalPrice,
)
export const getOriginalHandBags: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalHandBags,
)
export const getOriginalHoldBags: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalHoldBags,
)
export const getOriginalSearchBags: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.searchBags,
)
export const getOriginalPromocode: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalPromocode,
)

export const getOriginalInsurance: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalInsurance,
)

export const getOriginalPassengersNames: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalPassengersNames,
)
export const getOriginalEmail: $FlowFixMe = createSelector(
  getOriginals,
  originals => originals.originalEmail,
)

export const getSubmitting: $FlowFixMe = createSelector(
  getStates,
  states => states.submitting,
)
export const getSubmittingTimestamp: $FlowFixMe = createSelector(
  getStates,
  states => states.submittedTimestamp,
)
export const getFlightsChecked: $FlowFixMe = createSelector(
  getStates,
  states => states.flightsChecked,
)
export const getBillingDetails: $FlowFixMe = createSelector(
  getStates,
  states => states.billingDetails,
)
export const getMaxPassengers: $FlowFixMe = createSelector(
  getStates,
  states => states.maxPassengers,
)
export const getDocumentNeed: $FlowFixMe = createSelector(
  getStates,
  states => states.documentNeed,
)
export const getSandbox: $FlowFixMe = createSelector(
  getStates,
  states => states.sandbox,
)
export const getPassengers: $FlowFixMe = createSelector(
  getStates,
  states => states.passengers,
)
export const getFlightsCheckForced: $FlowFixMe = createSelector(
  getStates,
  states => states.flightsCheckForced,
)
export const getSessionValid: $FlowFixMe = createSelector(
  getSession,
  states => states.valid,
)
export const getSessionCreated: $FlowFixMe = createSelector(
  getSession,
  states => states.created,
)
export const getActiveStep: $FlowFixMe = createSelector(
  getStates,
  states => states.activeStep,
)
export const getMultistep: $FlowFixMe = createSelector(
  getStates,
  states =>
    fp.includes(states.reservationVariant, [
      RESERVATION_VARIANT.MULTISTEP,
      RESERVATION_VARIANT.MULTISTEP_ANCILLARIES,
    ]),
)
export const getReservationVariant: $FlowFixMe = createSelector(
  getStates,
  states => states.reservationVariant,
)
export const getPreBooking: $FlowFixMe = createSelector(
  getStates,
  states => states.preBooking,
)
